/**
 * Fuente única de verdad de las preguntas y opciones del form nativo de Derivaciones.
 *
 * Texto y opciones transcriptos LITERAL desde el form real de Tally (`tally.so/embed/3N0L2b`,
 * relevado vía `window.__NEXT_DATA__.props.pageProps.blocks`, sin enviar ningún submit —
 * 2026-09-18). Las claves (`key`) son las que espera el escenario clon de Make
 * ("Derivaciones nativas — alta vía webhook directo (CLON)", scenarioId 4927098) — ver
 * memoria del proyecto (`derivaciones-migracion-nativa.md`) para el mapeo completo contra
 * el escenario original.
 *
 * OJO: el texto de las opciones de las 8 preguntas de riesgo (`RISK_FIELDS`) es lo que el
 * escenario de Make compara con match EXACTO de texto para rutear (rama "Aprobó" vs
 * "Reprobó", más la rama "Suicidio" sobre `main_consultation_topic`). No parafrasear ni
 * "mejorar" ese texto — un cambio de una palabra rompe el ruteo de riesgo en silencio.
 */

export interface ChoiceOption {
  /** Texto exacto — es lo que se manda a Make, tiene que matchear la condición del escenario. */
  value: string;
}

export interface RiskField {
  key: string;
  detailKey: string;
  title: string;
  question: string;
  /** En orden de severidad creciente — el último es el que dispara "Aprobó" en Make. */
  levels: ChoiceOption[];
}

/** Las 8 preguntas de screening de riesgo — mismo orden que el form real. */
export const RISK_FIELDS: RiskField[] = [
  {
    key: 'risk_suicide_level',
    detailKey: 'risk_suicide_detail',
    title: 'Riesgo de Suicidio o Autolesiones',
    question: '¿El paciente presenta pensamientos suicidas activos o ha manifestado comportamientos autolesivos?',
    levels: [
      { value: 'No hay indicios' },
      { value: 'Pensamientos o comportamientos autolesivos y/o suicidas presentes, pero no activos' },
      { value: 'Pensamientos o comportamientos autolesivos y/o suicidas que pueden poner en riesgo a sí mismo o a otras personas.' },
    ],
  },
  {
    key: 'risk_psychosis_level',
    detailKey: 'risk_psychosis_detail',
    title: 'Trastornos Psicóticos Activos',
    question: '¿El paciente presenta síntomas psicóticos que afectan su percepción de la realidad, como alucinaciones o delirios?',
    levels: [
      { value: 'No presenta síntomas psicóticos' },
      { value: 'Síntomas psicóticos leves' },
      { value: 'Síntomas psicóticos severos que afectan significativamente su percepción de la realidad y requieren estabilización farmacológica (alucinaciones, delirios, despersonalización, alteración severa de la realidad, etc.).' },
    ],
  },
  {
    key: 'risk_crisis_level',
    detailKey: 'risk_crisis_detail',
    title: 'Crisis Emocionales Agudas',
    question: '¿El paciente está experimentando una crisis emocional que requiere intervención presencial inmediata?',
    levels: [
      { value: 'No presenta crisis emocional' },
      { value: 'Crisis emocional moderada, pero manejable' },
      { value: 'Crisis emocional aguda e incontrolable.' },
    ],
  },
  {
    key: 'risk_substance_abuse_level',
    detailKey: 'risk_substance_abuse_detail',
    title: 'Abuso de Sustancias Activo',
    question: '¿El paciente presenta un abuso de sustancias que afecta su funcionamiento diario?',
    levels: [
      { value: 'No hay abuso de sustancias' },
      { value: 'Abuso de sustancias presente, pero moderado' },
      { value: 'Abuso de sustancias severo que afecta significativamente el funcionamiento de la persona en su día a día.' },
    ],
  },
  {
    key: 'risk_medical_condition_level',
    detailKey: 'risk_medical_condition_detail',
    title: 'Condiciones médicas comórbidas severas',
    question: '(EJ: TCA graves, patologías cardíacas graves, etc): ¿El paciente tiene condiciones médicas que podrían complicar el tratamiento psicológico?',
    levels: [
      { value: 'No presenta condiciones médicas relevantes' },
      { value: 'Condiciones médicas presentes, pero controladas, leves' },
      { value: 'TCA grave que requiere asistencia médica inmediata. Riesgo de vida.' },
      { value: 'Condiciones médicas severas que complican el tratamiento psicológico online.' },
    ],
  },
  {
    key: 'risk_violent_relationship_level',
    detailKey: 'risk_violent_relationship_detail',
    title: 'Relaciones Violentas',
    question: '¿El paciente se encuentra inmerso en una relación violenta actualmente?',
    levels: [
      { value: 'No hay indicios' },
      { value: 'Ha experimentado relaciones violentas anteriormente, pero no en la actualidad.' },
      { value: 'El paciente se encuentra en una relación violenta que pone en riesgo su vida y/o la de otros actualmente.' },
    ],
  },
  {
    key: 'risk_homicidal_ideation_level',
    detailKey: 'risk_homicidal_ideation_detail',
    title: 'Agresores sexuales, ideación homicida o amenaza de hacer daños a otras personas',
    question: '¿El paciente presenta ideación homicida, amenaza de hacer daño a otras personas o agresividad sexual?',
    levels: [
      { value: 'No hay indicios' },
      { value: 'Presenta ideación homicida, amenaza de hacer daño a otras personas o agresividad sexual.' },
    ],
  },
  {
    key: 'risk_judicial_case_level',
    detailKey: 'risk_judicial_case_detail',
    title: 'Casos Judicializados',
    question: '¿El paciente está en un proceso judicial?',
    levels: [
      { value: 'No posee' },
      { value: 'Presentó un caso judicial que lo afectó anteriormente pero que en la actualidad está resuelto' },
      { value: 'Llegó a consulta por solicitud judicial.' },
    ],
  },
];

/** Las 19 temáticas — el valor "Suicidio" dispara la rama dedicada en Make (match exacto,
 *  case-insensitive "contiene"), sin importar las respuestas de riesgo. */
export const MAIN_CONSULTATION_TOPICS: ChoiceOption[] = [
  { value: 'Adicciones' },
  { value: 'Bipolaridad / TLP' },
  { value: 'Cuidados Paliativos' },
  { value: 'Dependencia emocional' },
  { value: 'Depresión' },
  { value: 'Desarrollo personal' },
  { value: 'Duelos' },
  { value: 'Embarazo, puerperio y primera infancia' },
  { value: 'Narcisismo y violencia' },
  { value: 'Obesidad y TCA' },
  { value: 'Relaciones personales' },
  { value: 'Sexualidad / Relaciones no - monógamas' },
  { value: 'Suicidio' },
  { value: 'TAG / Fobias' },
  { value: 'TDAH' },
  { value: 'TEA' },
  { value: 'Trauma / TEPT / Abuso sexual' },
  { value: 'TOC' },
  { value: 'Transexualidad / LGBTIQ+' },
];

export const DERIVATION_REASON_OPTIONS: ChoiceOption[] = [
  { value: 'Derivación a otra psicóloga de la clínica' },
  { value: 'Paciente no apto para dispositivo online' },
];

export const WHEN_COMMUNICATED_OPTIONS: ChoiceOption[] = [
  { value: 'En la última sesión' },
  { value: 'Hace dos sesiones' },
  { value: 'Hace mas de dos sesiones' },
];

export const FAMILY_SITUATION_OPTIONS: ChoiceOption[] = [
  { value: 'Vive solo/a' },
  { value: 'Vive con pareja' },
  { value: 'Vive con hijos y con pareja' },
  { value: 'Vive con hijos y sin pareja' },
  { value: 'Vive con otros familiares' },
  { value: 'Otro' },
];

export const WORK_SITUATION_OPTIONS: ChoiceOption[] = [
  { value: 'Empleado a tiempo completo' },
  { value: 'Empleado a tiempo parcial' },
  { value: 'Autónomo' },
  { value: 'Desempleado' },
  { value: 'Estudiante' },
  { value: 'Jubilado' },
  { value: 'Otro' },
];

export const SUPPORT_NETWORK_OPTIONS: ChoiceOption[] = [
  { value: 'Tiene una red de apoyo fuerte (amigos, familiares, etc.)' },
  { value: 'Cuenta con algunas pocas personas de sostén (>1)' },
  { value: 'Cuenta con solo una persona de sostén (solo 1)' },
  { value: 'No cuenta con ninguna persona de sostén (0)' },
];

export const MEDICAL_HISTORY_OPTIONS: ChoiceOption[] = [
  { value: 'No tiene antecedentes médicos relevantes' },
  { value: 'Ha tenido o tiene enfermedades crónicas' },
  { value: 'Ha recibido tratamiento psiquiátrico en el pasado' },
  { value: 'Desconozco sus antecedentes médicos' },
];

export const LIFESTYLE_OPTIONS: ChoiceOption[] = [
  { value: 'Estilo de vida bastante saludable (ejercicio regular, buena alimentación)' },
  { value: 'Estilo de vida moderadamente saludable' },
  { value: 'Estilo de vida poco saludable' },
  { value: 'Consumo de sustancias (tabaco, alcohol, otras)' },
];

export const EVOLUTION_OPTIONS: ChoiceOption[] = [
  { value: 'Mejora significativa' },
  { value: 'Mejora moderada' },
  { value: 'Sin cambios' },
  { value: 'Retroceso en algunos aspectos' },
  { value: 'Retroceso significativo' },
];

export const HAS_PSYCHIATRIST_OPTIONS: ChoiceOption[] = [
  { value: 'Tiene' },
  { value: 'No tiene' },
];

export const ALREADY_COMMUNICATED_OPTIONS: ChoiceOption[] = [
  { value: 'Si' },
  { value: 'No' },
];

/** Shape completo del payload que recibe el webhook clon (todas las claves que declara su interfaz). */
export interface DerivationFormValues {
  psychologist_email: string;
  patient_email: string;
  patient_name: string;
  process_id: string;
  sessions_count_grade: string;

  already_communicated_to_patient: string;
  when_communicated_to_patient: string;
  derivation_reason: string;
  main_consultation_topic: string;

  emergency_contact_relationship_has: string;
  emergency_contact_relationship: string;
  has_psychiatrist_contact: string;
  psychiatrist_phone: string;
  family_situation: string;
  work_situation: string;
  work_situation_other_detail: string;
  occupation: string;
  career_studying: string;
  support_network: string;
  relevant_medical_history: string;
  lifestyle_habits: string;
  main_problems_addressed: string;
  patient_evolution: string;
  recommendations_for_new_therapist: string;
  why_patient_requested_derivation: string;
  main_consultation_topic_2: string;

  risk_suicide_level: string;
  risk_suicide_detail: string;
  risk_psychosis_level: string;
  risk_psychosis_detail: string;
  risk_crisis_level: string;
  risk_crisis_detail: string;
  risk_substance_abuse_level: string;
  risk_substance_abuse_detail: string;
  risk_medical_condition_level: string;
  risk_medical_condition_detail: string;
  risk_violent_relationship_level: string;
  risk_violent_relationship_detail: string;
  risk_homicidal_ideation_level: string;
  risk_homicidal_ideation_detail: string;
  risk_judicial_case_level: string;
  risk_judicial_case_detail: string;
}

export function emptyDerivationFormValues(): DerivationFormValues {
  return {
    psychologist_email: '',
    patient_email: '',
    patient_name: '',
    process_id: '',
    sessions_count_grade: '',
    already_communicated_to_patient: '',
    when_communicated_to_patient: '',
    derivation_reason: '',
    main_consultation_topic: '',
    emergency_contact_relationship_has: '',
    emergency_contact_relationship: '',
    has_psychiatrist_contact: '',
    psychiatrist_phone: '',
    family_situation: '',
    work_situation: '',
    work_situation_other_detail: '',
    occupation: '',
    career_studying: '',
    support_network: '',
    relevant_medical_history: '',
    lifestyle_habits: '',
    main_problems_addressed: '',
    patient_evolution: '',
    recommendations_for_new_therapist: '',
    why_patient_requested_derivation: '',
    main_consultation_topic_2: '',
    risk_suicide_level: '',
    risk_suicide_detail: '',
    risk_psychosis_level: '',
    risk_psychosis_detail: '',
    risk_crisis_level: '',
    risk_crisis_detail: '',
    risk_substance_abuse_level: '',
    risk_substance_abuse_detail: '',
    risk_medical_condition_level: '',
    risk_medical_condition_detail: '',
    risk_violent_relationship_level: '',
    risk_violent_relationship_detail: '',
    risk_homicidal_ideation_level: '',
    risk_homicidal_ideation_detail: '',
    risk_judicial_case_level: '',
    risk_judicial_case_detail: '',
  };
}

/** true si esta pregunta de riesgo está en su nivel MÁS severo (el que dispara "Aprobó" en Make). */
export function isMostSevere(field: RiskField, value: string): boolean {
  return value === field.levels[field.levels.length - 1].value;
}
